import { Close, Content, Portal, Dialog } from "@radix-ui/react-dialog";
import styled, { keyframes } from "styled-components";

import Icon from "app/components/shared/Icon";
import Emojify from "app/components/shared/Emojify";

import { CommandStep } from "app/lib/pipeline";

// styled components are only doing animation for the time being
// to be replaced with fully tailwinded components when ready
const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform:translateX(0); }
`;

const slideOut = keyframes`
  from { transform:translateX(0); }
  to{ transform: translateX(100%); }
`;

const StyledContent = styled(Content)`
  will-change: transform;

  &[data-state="open"] {
    animation: ${slideIn} 150ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  &[data-state="closed"] {
    animation: ${slideOut} 150ms cubic-bezier(0.22, 1, 0.36, 1);
  }
`;

const Section = ({ icon, heading, children }) => (
  <div className="px-4">
    <h4>
      <Icon className="inline-flex items-center gap-1 text-sm" icon={icon} />
      {heading}
    </h4>
    {children}
  </div>
);

function handleInteractOutside(event) {
  event.preventDefault();
}

const SidePanelContent = ({ data }: { data: CommandStep }) => (
  <Portal>
    <StyledContent
      onInteractOutside={handleInteractOutside}
      className="shadow-md fixed top-16 bottom-8 right-8 w-64 bg-white rounded-md"
    >
      <Close className="absolute top-3 right-3">
        <Icon icon="close" />
      </Close>

      <h3 className="text-base font-medium p4 ">
        <Emojify text={data.label} className="inline-flex items-center gap-1" />
      </h3>
      <div className="divide-y">
        {(data.command || data.commands) && (
          <Section
            icon="heroicons/solid/code-bracket-square"
            heading="Commands"
          >
            {data.command || data.commands}
          </Section>
        )}
        {data.branches && (
          <Section icon="custom/outline/16x/branch" heading="Branch filtering">
            {data.branches}
          </Section>
        )}
        {data.artifact_paths && (
          <Section icon="heroicons/solid/archive-box" heading="Artifact paths">
            {data.artifact_paths}
          </Section>
        )}
      </div>
    </StyledContent>
  </Portal>
);

type SidePanelProps = {
  open: boolean;
  setOpen: (value: React.SetStateAction<boolean>) => void;
  data: CommandStep | undefined;
};

const SidePanel = ({ open, setOpen, data }: SidePanelProps) => (
  <Dialog modal={false} open={open} onOpenChange={setOpen}>
    {data && <SidePanelContent data={data} />}
  </Dialog>
);

export default SidePanel;
